import * as React from 'react'
import { useCallback, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Copy, CopyCheck, ExternalLink, Trash2 } from 'lucide-react'
import { ArtifactPreview } from '@renderer/components/artifact-preview'
import { cleanArtifactTextForCopy } from '../utils/clean-artifact-text'
import { copyTextToClipboard } from '@renderer/utils/clipboard'
import { Cell, Logo } from '@highlight/ui'
import { providerLogo } from '@renderer/utils/provider-logo'

/**
 * Extracts text content from React children, handling embedded artifacts
 */
function extractTextFromChildren(children: React.ReactNode): string {
  if (typeof children === 'string') {
    // Clean artifact tags from string content
    return cleanArtifactTextForCopy(children)
  }

  if (typeof children === 'number') {
    return children.toString()
  }

  if (Array.isArray(children)) {
    return children.map(extractTextFromChildren).join('')
  }

  if (React.isValidElement(children)) {
    // If it's an artifact preview, skip it completely
    if (children.type === ArtifactPreview) {
      return ''
    }

    // For other elements, try to extract text from their children
    if (children.props?.children) {
      return extractTextFromChildren(children.props.children)
    }
  }

  return ''
}

interface SummaryListMarkdownProps extends Omit<React.ComponentProps<'li'>, 'onClick'> {
  children?: React.ReactNode
  onClick?: (props: Record<string, unknown>) => void
  node?: unknown
  disabled?: boolean
}

export function SummaryListMarkdown({
  className,
  children,
  onClick,
  node,
  disabled,
  ...props
}: SummaryListMarkdownProps) {
  // `Markdown` reshapes a GFM task item into `li[data-checked]`, dropping the `task-list-item` class.
  const isTask = (props as Record<string, unknown>)['data-checked'] !== undefined

  const onClickItem = useCallback(
    (_e: React.MouseEvent<HTMLElement>) => {
      // Block clicks when disabled
      if (disabled) return

      // Only handle clicks for task list items
      if (isTask && onClick) {
        onClick({ children, node, ...props })
      }
    },
    [children, onClick, node, props, isTask, disabled],
  )

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- task list items act as interactive toggles
    <li
      {...props}
      className={twMerge(
        'text-light/40 group/list-item relative isolate min-h-6 w-fit text-left leading-6 no-underline transition-colors',
        isTask && !disabled && 'cursor-pointer list-none',
        isTask && disabled && 'cursor-not-allowed list-none opacity-50',
        className,
      )}
      onClick={onClickItem}
      onKeyDown={
        isTask
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClickItem(e as unknown as React.MouseEvent<HTMLElement>)
              }
            }
          : undefined
      }
      role={isTask ? 'button' : undefined}
      tabIndex={isTask ? 0 : undefined}
    >
      {children}
    </li>
  )
}

interface SummaryInputMarkdownProps extends Omit<React.ComponentProps<'div'>, 'onClick'> {
  node: unknown
  inputProps: React.ComponentProps<'input'>
  onClick?: (props: { lineNumber: number }) => void
  onDelete?: (props: { lineNumber: number }) => void
  disabled?: boolean
}

export function SummaryInputMarkdown({
  className,
  inputProps,
  onClick,
  onDelete,
  node: _node,
  disabled,
  ...props
}: SummaryInputMarkdownProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    // Find the parent li and extract text from it
    const target = e.currentTarget as HTMLElement
    const liElement = target.closest('li[data-checked]')
    if (liElement) {
      const text = extractTextFromChildren(liElement.textContent)
      copyTextToClipboard(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [])

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      if (!onDelete || disabled) return

      // Find the parent li element and extract line number
      const target = e.currentTarget as HTMLElement
      const liElement = target.closest('li[data-checked]')
      if (liElement) {
        const lineNumberStr = liElement.getAttribute('data-line-number')
        const lineNumber = lineNumberStr ? parseInt(lineNumberStr, 10) : undefined

        if (lineNumber !== undefined) {
          onDelete({ lineNumber })
        }
      }
    },
    [onDelete, disabled],
  )

  if (inputProps.type !== 'checkbox') {
    return <input {...inputProps} />
  }

  const handleClick = (e: React.MouseEvent) => {
    // Block clicks when disabled
    if (!onClick || disabled) return

    // Find the parent task item
    const target = e.currentTarget as HTMLElement
    const liElement = target.closest('li[data-checked]')

    if (liElement) {
      // Extract the line number from the data attribute
      const lineNumberStr = liElement.getAttribute('data-line-number')
      const lineNumber = lineNumberStr ? parseInt(lineNumberStr, 10) : undefined

      if (lineNumber !== undefined) {
        onClick({ lineNumber })
      }
    }
  }

  return (
    <>
      <div
        className={twMerge(
          'relative me-1.5 mt-[2px] inline-flex h-5 w-5 align-top',
          'before:absolute before:bottom-[-6px] before:left-[-6px] before:right-[-6px] before:top-[-6px] before:content-[""]',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className,
        )}
        onClick={(e) => {
          e.stopPropagation()
          // The wrapper sits in the task item's label, which would otherwise click its input too.
          e.preventDefault()
          handleClick(e)
        }}
        {...props}
      >
        <input
          {...inputProps}
          readOnly
          disabled={disabled}
          className={twMerge(
            // base box
            'm-0',
            'h-5 w-5 appearance-none rounded-md border',
            'border-light/5 bg-light/5 group-hover/list-item:border-light/20',
            // checked state
            'checked:text-light peer',
            // focus
            'outline-none',
            // allow clicks to pass through to wrapper
            'pointer-events-none',
          )}
        />
        <svg
          width="12"
          height="9"
          viewBox="0 0 12 9"
          className={
            'pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 peer-checked:block'
          }
        >
          <path
            fill={'currentColor'}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.12903 8.21331C5.39368 8.99555 4.16386 8.99555 3.4285 8.21331L0.710429 5.32193C0.283329 4.8676 0.299817 4.14773 0.747255 3.71405C1.19469 3.28037 1.90365 3.29711 2.33075 3.75144L4.77877 6.35554L9.67043 1.152C10.0975 0.697667 10.8065 0.680926 11.2539 1.11461C11.7014 1.54829 11.7178 2.26816 11.2907 2.72249L6.12903 8.21331Z"
          />
        </svg>
      </div>
      <div
        className={twMerge(
          'absolute right-0 top-1/2 z-10 -translate-y-1/2 px-1 py-0.5',
          'bg-background-overlay inline-flex items-center rounded-full shadow-md',
          'opacity-0 transition-opacity group-hover/list-item:opacity-100',
        )}
      >
        <button
          onClick={handleCopy}
          className={twMerge(
            'inline-flex size-6 items-center justify-center bg-transparent',
            'text-foreground-placeholder hover:text-foreground-secondary transition-colors',
          )}
          aria-label="Copy todo"
          type="button"
        >
          {copied ? <CopyCheck size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={2} />}
        </button>
        <button
          onClick={handleDelete}
          className={twMerge(
            'inline-flex size-6 items-center justify-center bg-transparent',
            'text-foreground-placeholder hover:text-foreground-secondary transition-colors',
          )}
          aria-label="Delete todo"
          type="button"
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>
    </>
  )
}

interface ArtifactLinkMarkdownProps extends React.ComponentProps<'a'> {
  href?: string
  children?: React.ReactNode
}

// Map of external app deeplink protocols to their URL schemes
const EXTERNAL_APP_PROTOCOLS: Record<string, string> = {
  NOTION: 'notion',
  LINEAR: 'linear',
  SLACK: 'slack',
  FIGMA: 'figma',
  GITHUB: 'github',
  ASANA: 'asana',
  JIRA: 'jira',
  TRELLO: 'trello',
}

export function ArtifactLinkMarkdown({ href, children, className, ...props }: ArtifactLinkMarkdownProps) {
  // Handle case where artifact URL is in the text instead of href (e.g., [CONVERSATION://id]())
  const childrenText = typeof children === 'string' ? children : ''
  const isArtifactInText =
    childrenText.startsWith('CONVERSATION://') ||
    childrenText.startsWith('AUDIO_NOTE://') ||
    childrenText.startsWith('INSIGHT://')
  if (isArtifactInText) {
    return <ArtifactPreview url={childrenText}>{children}</ArtifactPreview>
  }

  if (!href) {
    return <span className={className}>{children}</span>
  }
  // Handle https://artifact.local/ URL format
  // This is used by the backend when generating summaries
  if (href.startsWith('https://artifact.local/')) {
    const localMatch = href.match(/https:\/\/artifact\.local\/(CONVERSATION|AUDIO_NOTE|INSIGHT)\/(.+)/)
    if (localMatch) {
      const [, type, id] = localMatch
      const artifactUrl = `${type}://${id}`
      return <ArtifactPreview url={artifactUrl}>{children}</ArtifactPreview>
    }
  }

  // Handle https://deeplink.local/ URL format for external app deeplinks
  // These are converted from protocols like NOTION://, LINEAR://, SLACK://, etc.
  if (href.startsWith('https://deeplink.local/')) {
    const deeplinkMatch = href.match(/https:\/\/deeplink\.local\/([A-Z]+)\/(.+)/)
    if (deeplinkMatch) {
      const [, appType, encodedPath] = deeplinkMatch
      const protocol = EXTERNAL_APP_PROTOCOLS[appType]
      if (protocol) {
        const decodedPath = decodeURIComponent(encodedPath)
        const deeplink = `${protocol}://${decodedPath}`

        const handleClick = (e: React.MouseEvent) => {
          e.preventDefault()
          e.stopPropagation()
          // Open the deeplink - this will trigger the OS to open the appropriate app
          window.open(deeplink, '_blank')
        }

        const logo = providerLogo(protocol)
        const icon = (
          <Cell.Icon size="small">{logo ? <Logo name={logo} size={12} /> : <ExternalLink size={12} />}</Cell.Icon>
        )
        return (
          <a
            href={deeplink}
            onClick={handleClick}
            className={twMerge(
              'text-foreground-secondary hover:text-foreground inline-flex cursor-pointer items-center gap-1 break-words hover:underline',
              className,
            )}
            {...props}
          >
            {icon}
            {children}
          </a>
        )
      }
    }
  }

  // Check if this is an artifact URL in href (native protocol format)
  if (href.startsWith('CONVERSATION://') || href.startsWith('AUDIO_NOTE://') || href.startsWith('INSIGHT://')) {
    return <ArtifactPreview url={href}>{children}</ArtifactPreview>
  }

  // Regular link behavior for non-artifact URLs
  return (
    <a
      href={href}
      className={twMerge('text-foreground-secondary hover:text-foreground break-words hover:underline', className)}
      {...props}
    >
      {children}
    </a>
  )
}
